import { AnimatedComponent } from '@react-spring/web';
import React from 'react';

import { useTickTransition } from '../../hooks/useTickTransition';
import { ExtendAndOverride } from '../../utils/ExtendAndOverride';
import { NumberCoercible } from '../../utils/NumberCoercible';
import { StringCoercible } from '../../utils/StringCoercible';
import { ScaleWithValueType } from '../../utils/SupportedScale';
import { CrispLine } from '../utils/CrispLine';

export interface GridLinesOwnProps<T> {
  /**
   * Whether the grid lines should be oriented vertically rather than horizontally. Defaults to
   * false.
   */
  isVertical: boolean;
  /**
   * The starting position to render each grid line along the opposite axis. For example, for
   * vertical grid lines, this specifies the starting y position of each line.
   */
  start: number;
  /**
   * The ending position to render each grid line along the opposite axis. For example, for vertical
   * grid lines, this specifies the ending y position of each line.
   */
  end: number;
  /**
   * A d3 scale used to scale the given values.
   */
  scale: ScaleWithValueType<T>;
  /**
   * The set of values for which to show grid lines.
   */
  values: T[];
  /**
   * Whether animations are enabled.
   */
  isAnimated: boolean;
}

export type GridLinesProps<T> = ExtendAndOverride<
  GridLinesOwnProps<T>,
  Omit<React.ComponentPropsWithoutRef<AnimatedComponent<'line'>>, 'css'>
>;

/**
 * Component to render a set of lines at given positions in a particular orientation, as part of the
 * background of a grid.
 */
export const GridLines = <T extends NumberCoercible | StringCoercible>({
  scale,
  values: valuesProp,
  start,
  end,
  isAnimated,
  isVertical,
  ...lineProps
}: GridLinesProps<T>): JSX.Element => {
  const transition = useTickTransition({ ticks: valuesProp, scale, isAnimated });

  return transition((transitionProps) => {
    const { interpolatedValue, opacity } = transitionProps;
    return (
      <CrispLine
        {...lineProps}
        x1={isVertical ? start : interpolatedValue}
        x2={isVertical ? end : interpolatedValue}
        y1={isVertical ? interpolatedValue : start}
        y2={isVertical ? interpolatedValue : end}
        opacity={opacity}
      />
    );
  });
};
